import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { config } from "../config.js";

const SITE_NAME = "Community Ninja";
const DEFAULT_TITLE = "Reddit Monitoring for Customer Discovery | Community Ninja";
const DEFAULT_DESCRIPTION =
  "Community Ninja monitors Reddit everyday and finds relevant conversations about your product, market, competitors, customers, and pain points, so you can engage where it counts.";
const DEFAULT_KEYWORDS = [
  "reddit monitoring",
  "customer discovery",
  "social listening",
  "reddit lead generation",
  "competitor tracking",
  "product research",
  "community-led growth",
  "reddit alerts",
];
const DEFAULT_IMAGE = "/assets/share-image-new.png";
const DEFAULT_IMAGE_ALT =
  "Community Ninja dashboard showing relevant Reddit conversations for customer discovery";
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;
const DEFAULT_LOCALE = "en_US";
const TWITTER_HANDLE = "@communityninja";

/**
 * One <Seo /> per top-level route. Public pages get full
 * title/description/OG/Twitter/canonical/JSON-LD; authenticated screens
 * pass `noindex` so dashboards stay out of search results.
 *
 * Props:
 *   title        — page title, joined to the site name automatically
 *   description  — meta description (string, kept under ~160 chars)
 *   image        — OG/Twitter image (absolute URL or path under /assets)
 *   imageAlt     — alt text for the OG/Twitter image
 *   keywords     — string[] of keywords for the meta tag
 *   type         — og:type ("website" by default, "article" for blog posts)
 *   publishedAt  — ISO date for article:published_time
 *   modifiedAt   — ISO date for article:modified_time
 *   author       — string for article:author (defaults to "Community Ninja")
 *   noindex      — set true on authed surfaces
 *   jsonLd       — object or array of objects to drop into <script type="application/ld+json">
 *
 * Usage:
 *   <Seo title="Opportunities" noindex />
 *   <Seo
 *     title="Reddit Monitoring for Customer Discovery"
 *     description="..."
 *     image="/assets/og-home.png"
 *     jsonLd={{ "@context": "https://schema.org", ... }}
 *   />
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  keywords,
  type = "website",
  publishedAt,
  modifiedAt,
  author = SITE_NAME,
  noindex = false,
  jsonLd = null,
}) {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = `${config.siteUrl}${location.pathname}`;
  const absImage = image?.startsWith("http")
    ? image
    : `${config.siteUrl}${image || DEFAULT_IMAGE}`;
  const keywordList = Array.isArray(keywords) && keywords.length > 0
    ? keywords
    : DEFAULT_KEYWORDS;
  const keywordContent = keywordList.join(", ");

  const isArticle = type === "article";
  const jsonLdList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      <meta name="keywords" content={keywordContent} />
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:width" content={String(DEFAULT_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(DEFAULT_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:locale" content={DEFAULT_LOCALE} />

      {isArticle && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {isArticle && (modifiedAt || publishedAt) && (
        <meta
          property="article:modified_time"
          content={modifiedAt || publishedAt}
        />
      )}
      {isArticle && author && (
        <meta property="article:author" content={author} />
      )}
      {isArticle &&
        keywordList.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {jsonLdList.map((entry, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
